import { formatShortDate } from '@app/shared'

import type { LearningSession } from '../../lib/data'
import { durationPhrase } from './derive'

// ============================================================================
// THE SESSION LOG — docs/design/handoff/"LifeOS Learning.dc.html", option 9b.
//
// Every day this track was worked on: the date, what was finished, how long it took, and the
// owner's own note when he wrote one.
//
// A ROW WITH A NOTE GETS THE `surface-2` GROUND — that is the mockup's rule, row for row, and it
// is a good one: the notes are the only part of this list that is his own writing rather than the
// system's bookkeeping, so they are what the eye should catch scrolling.
//
// AND A ROW WITHOUT ONE SHOWS NOTHING. No "no note", no placeholder line, no grey italic. He did
// not write anything that day and the log says so by saying nothing.
// ============================================================================

export function SessionLog({
  sessions,
  totalMinutes,
}: {
  sessions: LearningSession[]
  totalMinutes: number
}) {
  return (
    <section className="flex min-h-0 flex-1 flex-col gap-[11px] overflow-hidden rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex flex-none items-center gap-[10px]">
        <h2 className="text-[12.5px] font-semibold leading-none">Session log</h2>
        <span className="text-micro font-normal text-microcopy">
          {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'} ·{' '}
          {durationPhrase(totalMinutes)}
        </span>
        <button
          type="button"
          disabled
          title="Exporting the log arrives with V3 (the track-ingestion API), where the sessions live in the database rather than in this dataset. The same rows are already readable through the API."
          className="ml-auto cursor-not-allowed text-micro font-medium text-muted"
        >
          Export
        </button>
      </header>

      {sessions.length === 0 ? (
        <p className="text-[12px] leading-[1.45] text-microcopy text-pretty">
          Nothing has been logged against this plan yet. The first session opens this list.
        </p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-[2px] overflow-y-auto pr-2 [margin-right:-8px]">
          {sessions.map((session) => (
            <article
              key={session.date}
              className={[
                'flex flex-none items-start gap-3 rounded-tile px-[11px] py-[9px]',
                session.note ? 'bg-surface-2' : '',
              ].join(' ')}
            >
              <span className="w-[44px] flex-none text-micro font-semibold leading-[1.4] text-ink-2">
                {formatShortDate(session.date)}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
                <p className="text-[11.5px] font-medium leading-[1.3] text-ink-2 text-pretty">
                  {session.items.join(' · ')}
                </p>
                {session.note && (
                  <p className="text-micro font-normal leading-[1.4] text-muted text-pretty">
                    {session.note}
                  </p>
                )}
              </div>
              <div className="flex-none text-right">
                <p className="text-micro font-semibold leading-none text-ink">
                  {session.minutes} min
                </p>
                <p className="mt-1 text-[10px] font-normal leading-none text-microcopy">
                  {session.items.length} {session.items.length === 1 ? 'item' : 'items'}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}
